import React, { useState, useEffect, useCallback } from "react";
import { Button } from "react-bootstrap";
import { Dalle } from "../base/Dalle";
import { MISSION_CONFIG, MISSION_IMAGE_RATING_LABELS } from "./missionConfig";

function AfterImageBox({
  missionName,
  missionDate,
  missionSignificance,
  switchTab,
  setPendingGptPrompt,
  workshopId,
  studentId,
}) {
  const [rating, setRating] = useState(0);
  const [ratingLocked, setRatingLocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submitRating = useCallback(async (val) => {
    const wid =
      workshopId && String(workshopId) !== "unknown" ? String(workshopId) : "";
    const sid =
      studentId != null && studentId !== -1 ? String(studentId) : "";
    const pwd = localStorage.getItem(MISSION_CONFIG.storageKeys.password);
    if (!wid || !sid || !pwd) {
      return false;
    }
    try {
      const res = await fetch("/submit-mission-image-rating", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password: pwd,
          workshop_id: wid,
          student_id: sid,
          rating: val,
        }),
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        return false;
      }
      if (res.ok && (data.status === "ok" || data.status === "already_recorded")) {
        if (data.status === "already_recorded" && data.rating != null) {
          setRating(Number(data.rating));
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [workshopId, studentId]);

  const handlePick = async (val) => {
    if (ratingLocked || submitting) return;
    setRating(val);
    setSubmitting(true);
    const ok = await submitRating(val);
    setSubmitting(false);
    if (ok) {
      setRatingLocked(true);
    }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)", width: "100%" }}>
          <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
          <p style={{ margin: "0 0 4px 0" }}>
            <Button variant="outline-primary" size="sm" onClick={() => window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(missionName + ' ' + missionDate + ' ' + missionSignificance)}`, '_blank')} style={{ margin: "0 5px 0 0", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease" }}>
              Search
            </Button>
            {" "}images of <span style={{ color: "#f05f40", fontWeight: "bold" }}>{missionName}</span> and compare them to the AI version above.
          </p>
          <div style={{
            textAlign: "center",
            margin: "14px 0 6px 0",
            fontSize: "1.15em",
            fontWeight: "bold",
            color: "#333",
            letterSpacing: "0.5px",
          }}>
            How realistic is the AI image?
          </div>
          <div style={{ marginTop: "8px" }}>
            <div
              className={`rating-scale ${rating === 0 ? "animated-outline" : ""}`}
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "stretch",
                gap: "0",
                maxWidth: "420px",
                margin: "0 auto",
                borderRadius: "10px",
                border: rating > 0 ? "2px solid transparent" : undefined,
                overflow: "hidden",
              }}
            >
              {[1, 2, 3, 4, 5].map((val) => {
                const isSelected = rating === val;
                const isFirst = val === 1;
                const isLast = val === 5;
                return (
                  <div
                    key={val}
                    className={`rating-btn${isSelected ? " rating-btn-selected" : ""}`}
                    onClick={() => handlePick(val)}
                    style={{
                      flex: "1 1 0",
                      minWidth: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      cursor: ratingLocked || submitting ? "default" : "pointer",
                      opacity: ratingLocked && !isSelected ? 0.45 : 1,
                      padding: "12px 4px 10px",
                      borderRadius: isFirst ? "8px 0 0 8px" : isLast ? "0 8px 8px 0" : "0",
                      backgroundColor: isSelected ? "#f05f40" : "#fafafa",
                      borderTop: "none",
                      borderBottom: "none",
                      borderLeft: isFirst ? "none" : isSelected ? "2px solid #d94e35" : "1px solid #e0e0e0",
                      borderRight: isLast ? "none" : "none",
                      position: "relative",
                      zIndex: isSelected ? 1 : 0,
                      boxShadow: isSelected ? "0 2px 8px rgba(240, 95, 64, 0.35)" : "none",
                    }}
                  >
                    <span style={{
                      fontSize: "1.3rem",
                      fontWeight: "bold",
                      color: isSelected ? "white" : "#555",
                      lineHeight: 1,
                    }}>
                      {val}
                    </span>
                    <span style={{
                      fontSize: "0.6rem",
                      color: isSelected ? "rgba(255,255,255,0.95)" : "#888",
                      fontWeight: "600",
                      marginTop: "5px",
                      textAlign: "center",
                      lineHeight: "1.25",
                      letterSpacing: "0.02em",
                    }}>
                      {MISSION_IMAGE_RATING_LABELS[val - 1]}
                    </span>
                  </div>
                );
              })}
            </div>
            {rating > 0 && (
              <div style={{ margin: "12px 0 0 0" }}>
                <p style={{ margin: "0", textAlign: "center" }}>
                  <strong>Refine your image prompt to create a more accurate historic mission image.</strong>
                </p>
                <br />
                <p style={{ margin: "0", textAlign: "left" }}>
                  Then return to{" "}
                  <Button variant="outline-primary" size="sm" onClick={() => { if (setPendingGptPrompt) setPendingGptPrompt(2); switchTab("gpt"); }} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease", borderColor: "#f05f40", color: "#f05f40" }}>
                    Text
                  </Button>{" "}
                  to discover how it sounded.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function NextGenAfterImageBox({ missionName, onCreatePoster }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)", width: "100%" }}>
          <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
          <p style={{ margin: "0" }}>
            Your <em>Next Generation</em> <span style={{ color: "#f05f40", fontWeight: "bold" }}>{missionName}</span> mission has been greenlit and is ready to present to Congress.
            <br /><br />
            Select{" "}
            <Button className="stageone-btn stageone-btn-glow poster-pulse dalle-create-poster-btn" onClick={onCreatePoster}>
              {MISSION_CONFIG.dalle.createPosterButtonText}
            </Button>{" "}
            to turn your design into a presentation poster.
          </p>
        </div>
      </div>
    </>
  );
}

export default function MissionDalle(props) {
  const missionName = props.missionData ? props.missionData.name : 'Unknown Mission';
  const missionDate = props.missionData ? props.missionData.date : '';
  const missionSignificance = props.missionData ? props.missionData.significance : '';

  const [posterMode, setPosterMode] = useState(() => {
    const saved = localStorage.getItem('mission-dalle-poster-mode');
    return saved === 'true';
  });

  useEffect(() => {
    if (props.posterModeUnlocked) {
      setPosterMode(true);
    }
  }, [props.posterModeUnlocked]);

  useEffect(() => {
    localStorage.setItem('mission-dalle-poster-mode', posterMode.toString());
  }, [posterMode]);

  const workshopConfig = {
    ...MISSION_CONFIG.dalle,
    hideModButtonsForSource: (sourcePromptIndex) => {
      return !(sourcePromptIndex != null && sourcePromptIndex >= 3);
    },
    afterImageContent: ({ onCreatePoster, sourcePromptIndex, workshopId, studentId }) => {
      const isPosterImage = sourcePromptIndex != null && sourcePromptIndex >= 3;
      if (isPosterImage) {
        return (
          <NextGenAfterImageBox
            missionName={missionName}
            onCreatePoster={onCreatePoster}
          />
        );
      }
      return (
        <AfterImageBox
          missionName={missionName}
          missionDate={missionDate}
          missionSignificance={missionSignificance}
          switchTab={props.switchTab}
          setPendingGptPrompt={props.setPendingGptPrompt}
          workshopId={workshopId}
          studentId={studentId}
        />
      );
    },
  };

  return (
    <Dalle
      {...props}
      workshopConfig={workshopConfig}
      storageKey={MISSION_CONFIG.storageKeys.dalleOutputs}
    />
  );
}
